/**
 * Rotas do painel de Ads (services/promocoes/servico.py, bloco /api/ads/*): same-origin + sessão do BI.
 * O nginx faz `auth_request` e injeta o X-Token no POST — o browser nunca envia token nenhum.
 *
 *   GET  /api/ads/resumo?data_inicio&data_fim&store_key      KPIs do período + anterior + TACOS
 *   GET  /api/ads/campanhas?…                                 uma linha por campanha (farol)
 *   GET  /api/ads/anuncios?…&campaign_id=                     uma linha por anúncio em campanha
 *   GET  /api/ads/curva-a-sem-ads?store_key                   curva A 30 d fora de campanha / hold
 *   GET  /api/ads/gasto-diario?…                              série diária + mês contra mês
 *   GET  /api/ads/coleta/status                               ads_coleta_status.json + rodando
 *   POST /api/ads/coleta/rodar                                dispara coletar_ads.py (admin)
 *
 * Tudo lê o retrato do Mongo; nada chama a API do Meli na hora.
 */
import type { AnuncioLinha, CampanhaLinha, ColetaStatus, CurvaALinha, GastoDiario, ResumoAds } from './logic'

export class AdsApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'AdsApiError'
    this.status = status
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, { credentials: 'include', cache: 'no-store', ...init })
  } catch {
    throw new AdsApiError(0, 'Serviço de Ads inacessível (é o mesmo processo das Promoções, porta 8101). Confira o proxy /api/ads/ e o serviço.')
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as { erro?: string; detail?: string | { msg?: string }; message?: string } | null
    const detail = typeof corpo?.detail === 'string' ? corpo.detail : corpo?.detail && typeof corpo.detail === 'object' ? corpo.detail.msg : undefined
    throw new AdsApiError(resp.status, corpo?.erro || detail || corpo?.message || `Falha Ads (HTTP ${resp.status}).`)
  }
  return resp.json() as Promise<T>
}

function q(params: Record<string, string | number | null | undefined>): string {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v))
  const t = s.toString()
  return t ? `?${t}` : ''
}

export function fetchResumo(ini: string, fim: string, storeKey: string): Promise<ResumoAds> {
  return api<ResumoAds>(`/api/ads/resumo${q({ data_inicio: ini, data_fim: fim, store_key: storeKey })}`)
}

export function fetchCampanhas(ini: string, fim: string, storeKey: string): Promise<{ campanhas: CampanhaLinha[] }> {
  return api(`/api/ads/campanhas${q({ data_inicio: ini, data_fim: fim, store_key: storeKey })}`)
}

export function fetchAnuncios(ini: string, fim: string, storeKey: string, campaignId?: number | null): Promise<{ anuncios: AnuncioLinha[] }> {
  return api(`/api/ads/anuncios${q({ data_inicio: ini, data_fim: fim, store_key: storeKey, campaign_id: campaignId ?? null })}`)
}

export function fetchCurvaA(storeKey: string): Promise<{ anuncios: CurvaALinha[]; janela: { ini: string; fim: string } }> {
  return api(`/api/ads/curva-a-sem-ads${q({ store_key: storeKey })}`)
}

export function fetchGastoDiario(ini: string, fim: string, storeKey: string): Promise<GastoDiario> {
  return api<GastoDiario>(`/api/ads/gasto-diario${q({ data_inicio: ini, data_fim: fim, store_key: storeKey })}`)
}

export function fetchColetaStatus(): Promise<ColetaStatus> {
  return api<ColetaStatus>('/api/ads/coleta/status')
}

export type RodarColeta = ColetaStatus & { ok: boolean; iniciado: boolean; ja_rodando: boolean; motivo?: string; erro?: string }

export function rodarColeta(): Promise<RodarColeta> {
  return api<RodarColeta>('/api/ads/coleta/rodar', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
}
